// Сюжет — сквозные ролики-трейлеры 16:9 (docs/story-videos.md): по конвенции
// ep1..ep6 перед уровнем нового инструмента (стыки наборов по 5 уровней).
// У Мира 1 сверх этого есть особый ПРОЛОГ
// (w01-prologue) — опенинг всей истории, играет первым перед самым первым
// уровнем. Привязка эпизод↔уровень — маркеры storyBefore/storyAfter уровней
// манифеста (конфиг карты, ими владеет сидер бэкенда); манифест без единого
// маркера (старый бэкенд) — деградация по той же конвенции, это не контракт
// (ср. collection.ts). Пролог — единичный клиентский спец-эпизод (не колонка
// уровня): он всегда «перед первым уровнем Мира 1». Анлок — из прогресса:
// before-эпизод открыт, когда доступен его уровень, after-эпизод — когда уровень
// пройден. Просмотренность (dg_story_v1) на анлок не влияет — только на
// авто-показ в PlayScreen. Файлы роликов: {mediaBaseUrl}/story/<id>.mp4.
//
// Вкладка «История» коллекции считается бэкендом (GET /api/v1/me/collection,
// см. @/api/collection + CollectionScreen); здесь остаются только маркеры
// роликов уровня, нужные PlayScreen (storyMarkersForLevel/storyVideoUrl).

import type { ManifestLevel } from '@/meta/useManifest';
import { LEVELS_PER_SET } from '@/save/unlock';

/** Мир-пролог и его особый эпизод: играет перед первым уровнем Мира 1. */
export const PROLOGUE_WORLD = 'w01';
export const PROLOGUE_EPISODE = `${PROLOGUE_WORLD}-prologue`;

/** Уровни, где открывается новый инструмент (rung 1–6, первый уровень каждого
 *  набора по LEVELS_PER_SET): 1, 6, 11, 16, 21, 26 — та же конвенция, что у
 *  сидера бэкенда (CatalogSeeder.StoryToolLevels). */
const STORY_TOOL_LEVELS = Array.from({ length: 6 }, (_, i) => i * LEVELS_PER_SET + 1);

/** Ролики уровня по моментам показа — упорядоченные очереди (перед первым
 *  уровнем Мира 1 это [пролог, интро рампы]). */
export interface LevelStoryMarkers { before: string[]; after: string[] }

type LevelLike = Pick<ManifestLevel, 'id' | 'world' | 'index'> &
  Partial<Pick<ManifestLevel, 'storyBefore' | 'storyAfter'>>;

/** Конвенция привязки (fallback без маркеров): зеркало сидера. After-роликов
 *  конвенция не знает — их даёт только явный маркер уровня из манифеста. */
function conventionMarkers(l: LevelLike): { before?: string; after?: string } {
  const n = STORY_TOOL_LEVELS.indexOf(l.index);
  return n >= 0 ? { before: `${l.world}-ep${n + 1}` } : {};
}

function rawMarkers(l: LevelLike, useConvention: boolean): { before?: string; after?: string } {
  return useConvention ? conventionMarkers(l) : { before: l.storyBefore, after: l.storyAfter };
}

/** Есть ли в манифесте хоть один маркер: нет ни одного = старый бэкенд, деградация. */
function withoutMarkers(levels: LevelLike[]): boolean {
  return !levels.some((l) => l.storyBefore || l.storyAfter);
}

/** id первого (минимального по index) уровня мира; null — если уровней нет. */
function firstLevelIdOfWorld(levels: LevelLike[], worldId: string): string | null {
  let best: LevelLike | null = null;
  for (const l of levels) if (l.world === worldId && (!best || l.index < best.index)) best = l;
  return best?.id ?? null;
}

interface MarkerCtx { useConvention: boolean; prologueLevelId: string | null }

function markerCtx(levels: LevelLike[]): MarkerCtx {
  return {
    useConvention: withoutMarkers(levels),
    prologueLevelId: firstLevelIdOfWorld(levels, PROLOGUE_WORLD),
  };
}

/** Очереди роликов уровня: обычные маркеры + пролог, прикреплённый к первому
 *  уровню Мира 1 (перед его before-роликом). */
function levelMarkers(l: LevelLike, ctx: MarkerCtx): LevelStoryMarkers {
  const m = rawMarkers(l, ctx.useConvention);
  const before = m.before ? [m.before] : [];
  const after = m.after ? [m.after] : [];
  // пролог — перед первым уровнем Мира 1; dedupe на случай, если маркер уровня
  // сам указывает на пролог (ручной/битый манифест) — иначе он сыграл бы дважды
  if (l.id === ctx.prologueLevelId && !before.includes(PROLOGUE_EPISODE)) {
    before.unshift(PROLOGUE_EPISODE);
  }
  return { before, after };
}

/** Ролики конкретного уровня — для показа в PlayScreen (очереди по моментам). */
export function storyMarkersForLevel(levels: LevelLike[], levelId: string): LevelStoryMarkers {
  const level = levels.find((l) => l.id === levelId);
  return level ? levelMarkers(level, markerCtx(levels)) : { before: [], after: [] };
}

/** URL ролика: S3-ключ story/<episodeId>.mp4 относительно MediaBaseUrl
 *  каталога ассетов (GET /api/v1/assets). Роликов-таблиц на бэкенде нет —
 *  конвенция ключа, как у медиа плееров. */
export function storyVideoUrl(mediaBaseUrl: string, episodeId: string): string {
  return `${mediaBaseUrl.replace(/\/+$/, '')}/story/${episodeId}.mp4`;
}
